/**
 * Boat renderer: vanilla BoatModel (128x64 sheet entity/boat/<wood>.png) built
 * with the shared ModelPart builder: bottom plate, four side walls and two
 * paddles that row while a rider paddles that side. One model per boat, drawn
 * at the interpolated position / yaw, tipped by the hit wobble.
 *
 * Transform chain = BoatRenderer: translate(0, 0.375, 0), rotate by yaw,
 * scale(-1,-1,1)/16, rotate Y by 90 deg (the model's bow points along +x).
 */

import * as THREE from 'three';
import type { EnvUniforms } from './env';
import { buildPart, entityMaterial, sheetTexture, setEffects, type CubeDef, type EntityMat, type PartDef } from './entities/core';
import type { Boat } from '../entities/vehicles/boat';

const PI = Math.PI;
const cube = (tex: [number, number], from: [number, number, number], size: [number, number, number]): CubeDef => ({ tex, from, size });

/** Vanilla BoatModel.createBodyModel parts (pixels, y down). */
const PARTS: Record<string, PartDef> = {
  bottom: { cubes: [cube([0, 0], [-14, -9, -3], [28, 16, 3])], pos: [0, 3, 1], rot: [PI / 2, 0, 0] },
  back: { cubes: [cube([0, 19], [-13, -7, -1], [18, 6, 2])], pos: [-15, 4, 4], rot: [0, PI * 1.5, 0] },
  front: { cubes: [cube([0, 27], [-8, -7, -1], [16, 6, 2])], pos: [15, 4, 0], rot: [0, PI / 2, 0] },
  right: { cubes: [cube([0, 35], [-14, -7, -1], [28, 6, 2])], pos: [0, 4, -9], rot: [0, PI, 0] },
  left: { cubes: [cube([0, 43], [-14, -7, -1], [28, 6, 2])], pos: [0, 4, 9], rot: [0, 0, 0] },
  leftPaddle: { cubes: [cube([62, 0], [-1, 0, -5], [2, 2, 18]), cube([62, 0], [-1.001, -3, 8], [1, 6, 7])], pos: [3, -5, 9], rot: [0, 0, PI / 16] },
  rightPaddle: { cubes: [cube([62, 20], [-1, 0, -5], [2, 2, 18]), cube([62, 20], [-1.001, -3, 8], [1, 6, 7])], pos: [3, -5, -9], rot: [0, PI, PI / 16] },
};

/** Vanilla water patch: a depth-only slab over the hull's inside, drawn after the boat so the sea does not fill it. */
const WATER_MASK: PartDef = { cubes: [cube([0, 0], [-14, -9, -3], [28, 16, 3])], pos: [0, -3, 1], rot: [PI / 2, 0, 0] };

interface Model { root: THREE.Group; tilt: THREE.Group; paddles: THREE.Group[]; mat: EntityMat; mask: THREE.Group; wood: number }

const lerp = (a: number, b: number, t: number): number => a + (b - a) * clamp01(t);
const clamp01 = (t: number): number => (t < 0 ? 0 : t > 1 ? 1 : t);

/** Vanilla BoatModel.animatePaddle. */
export function paddlePose(time: number, side: number): { x: number; y: number } {
  const x = lerp(-PI / 3, -PI / 12, (Math.sin(-time) + 1) / 2);
  let y = lerp(-PI / 4, PI / 4, (Math.sin(-time + 1) + 1) / 2);
  if (side === 1) y = PI - y;
  return { x, y };
}

export class BoatRenderer {
  readonly group = new THREE.Group();
  private readonly models = new Map<number, Model>();
  private readonly maskMat = new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: true });

  constructor(scene: THREE.Object3D, private readonly env: EnvUniforms) { scene.add(this.group); }

  private build(b: Boat): Model {
    const mat = entityMaterial(this.env, sheetTexture(`entity/boat/${b.texture}`));
    const root = new THREE.Group();              // world position + yaw
    const tilt = new THREE.Group();              // hit wobble about the bow axis
    const flip = new THREE.Group();              // scale(-1,-1,1)/16
    flip.scale.set(-1 / 16, -1 / 16, 1 / 16);
    const inner = new THREE.Group();             // bow along +x -> -z
    inner.rotation.y = PI / 2;
    root.add(tilt); tilt.add(flip); flip.add(inner);
    const paddles: THREE.Group[] = [];
    for (const [name, def] of Object.entries(PARTS)) {
      const g = buildPart(def, 128, 64, mat);
      inner.add(g);
      if (name === 'leftPaddle') paddles[0] = g;
      if (name === 'rightPaddle') paddles[1] = g;
    }
    const mask = buildPart(WATER_MASK, 128, 64, this.maskMat);
    mask.traverse((o) => { o.renderOrder = 1; });
    inner.add(mask);
    this.group.add(root);
    return { root, tilt, paddles, mat, mask, wood: b.wood };
  }

  update(boats: readonly Boat[], alpha: number, time = 0): void {
    const live = new Set<number>();
    for (const b of boats) {
      if (b.removed) continue;
      live.add(b.id);
      let m = this.models.get(b.id);
      if (!m) { m = this.build(b); this.models.set(b.id, m); }
      const x = b.px + (b.x - b.px) * alpha, y = b.py + (b.y - b.py) * alpha, z = b.pz + (b.z - b.pz) * alpha;
      // gentle idle bob on the water, off while the hull is driven
      const bob = b.status === 'water' && !b.occupied ? Math.sin(time * 2 + b.id) * 0.012 : 0;
      m.root.position.set(x, y + 0.375 + bob, z);
      let dyaw = b.yaw - b.pyaw;
      dyaw = Math.atan2(Math.sin(dyaw), Math.cos(dyaw));
      m.root.rotation.y = b.pyaw + dyaw * alpha;
      const wob = b.hurtTime > 0 ? (Math.sin(b.hurtTime - alpha) * (b.hurtTime - alpha) * b.damage * b.hurtDir) / 10 : 0;
      m.tilt.rotation.x = (wob * PI) / 180;
      for (let i = 0; i < 2; i++) {
        const t = b.paddlePrev[i] + (b.paddle[i] - b.paddlePrev[i]) * alpha;
        const p = paddlePose(t, i);
        m.paddles[i].rotation.x = p.x;
        m.paddles[i].rotation.y = p.y;
      }
      m.mask.visible = b.status !== 'under' && b.status !== 'underFlow';
      setEffects([m.mat], 1, false, 0, { x, y: y + 0.5, z });
    }
    for (const [id, m] of this.models) {
      if (live.has(id)) continue;
      this.group.remove(m.root);
      m.mat.dispose();
      this.models.delete(id);
    }
  }
}
